import { Activity, Deal, EntityType, MapEntity } from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import ActivityTypeWithCount from "@mapmycustomers/shared/types/entity/activities/ActivityTypeWithCount";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import { StageSummary } from "@mapmycustomers/shared/types/entity/deals/StageSummary";
import { MapEntry } from "@mapmycustomers/shared/types/map";
import PlatformBounds from "@mapmycustomers/shared/types/map/PlatformBounds";
import User from "@mapmycustomers/shared/types/User";
import ApiError from "@mapmycustomers/shared/util/api/ApiError";

import SourceType from "@app/enum/SourceType";
import { ActivityCount } from "@app/scene/home/component/Cards/util";
import { GroupByType } from "@app/scene/reports/components/Overview/components/Engagement/enum/GroupByType";
import SourceSummary from "@app/types/source/SourceSummary";
import Statistics from "@app/types/Statistics";

import { DealsGroupByType } from "../enum/DealsGroupByType";
import { DisplayType } from "../enum/DisplayType";
import { DealsGroupBy } from "../types/DealGroupBy";
import { DealsRottingFilter } from "../types/DealsRottingFilter";
import OpenDealsCardData from "../types/OpenDealsCardData";
import { SupportingGroupsRecordStats } from "../types/RecordsOutOfCadence";
import RecordsOutOfCadenceFilter from "../types/RecordsOutOfCadenceFilter";
import RecordsSourceFilter from "../types/RecordsSourceFilter";
import { RecordsStats } from "../types/RecordsStats";
import { createInitialDealsRotting } from "../util/createInitialDealsRotting";

export type HomeState = {
  activityByRepData: Record<User["id"], ActivityCount[]>;
  activityByRepDataError: ApiError | undefined;
  activityByRepDataLoading: boolean;
  activityByTypesDataError: ApiError | undefined;
  activityByTypesDataLoading: boolean;
  activityOverview: Statistics["values"];
  activityOverviewError: ApiError | undefined;
  activityOverviewLoading: boolean;
  activityTypes: ActivityType[];
  activityTypesWithCount: ActivityTypeWithCount[];
  dealsCount: number;
  dealsRotting: Record<DealsGroupBy["id"], RecordsStats>;
  dealsRottingError: ApiError | undefined;
  dealsRottingFilter: DealsRottingFilter;
  dealsRottingLoading: boolean;
  dealsRottingStagesStats: Record<Funnel["id"], Record<Stage["name"], RecordsStats>>;
  engagementRecordsList: MapEntity[];
  engagementRecordsLoading: boolean;
  error: ApiError | undefined;
  funnels: Funnel[];
  funnelStages: Stage[];
  funnelSummary: StageSummary[];
  funnelSummaryError: ApiError | undefined;
  funnelSummaryLoading: boolean;
  hasActivities: boolean;
  hasCompanies: boolean;
  loading: boolean;
  openDealsData: OpenDealsCardData;
  openDealsDataError: ApiError | undefined;
  openDealsDataLoading: boolean;
  recentCompaniesCriteria: {
    bounds?: PlatformBounds;
    limit: number;
    usersIds: User["id"][];
    zoom?: number;
  };
  recentCompaniesData: MapEntry[];
  recentCompaniesDataError: ApiError | undefined;
  recentCompaniesDataLoading: boolean;
  recordsBySourceList: MapEntity[];
  recordsBySourceListLoading: boolean;
  recordsBySourceSummary: SourceSummary[];
  recordsOutOfCadence: SupportingGroupsRecordStats;
  recordsOutOfCadenceError: ApiError | undefined;
  recordsOutOfCadenceFilter: RecordsOutOfCadenceFilter;
  recordsOutOfCadenceLoading: boolean;
  recordsSourceError: ApiError | undefined;
  recordsSourceFilter: RecordsSourceFilter;
  recordsSourceLoading: boolean;
  rottingDealsList: Deal[];
  rottingDealsLoading: boolean;
  rottingDealsTotal: number;
  selectedTypeData: Activity[];
  todayActivitiesData: Activity[];
  todayActivitiesDataError: ApiError | undefined;
  todayActivitiesDataForReps: Activity[];
  todayActivitiesDataForRepsError: ApiError | undefined;
  todayActivitiesDataForRepsLoading: boolean;
  todayActivitiesDataLoading: boolean;
  totalRecordsByActivityType: number;
  totalRecordsByEngagementList: number;
  totalRecordsBySourceList: number;
};

export const initialState: HomeState = {
  activityByRepData: [],
  activityByRepDataError: undefined,
  activityByRepDataLoading: false,
  activityByTypesDataError: undefined,
  activityByTypesDataLoading: false,
  activityOverview: {},
  activityOverviewError: undefined,
  activityOverviewLoading: false,
  activityTypes: [],
  activityTypesWithCount: [],
  dealsCount: 0,
  dealsRotting: createInitialDealsRotting(),
  dealsRottingError: undefined,
  dealsRottingFilter: {
    displayType: DisplayType.PERCENT,
    groupByType: DealsGroupByType.FUNNEL,
  },
  dealsRottingLoading: false,
  dealsRottingStagesStats: {},
  engagementRecordsList: [],
  engagementRecordsLoading: false,
  error: undefined,
  funnels: [],
  funnelStages: [],
  funnelSummary: [],
  funnelSummaryError: undefined,
  funnelSummaryLoading: false,
  hasActivities: false,
  hasCompanies: false,
  loading: false,
  openDealsData: {
    deals: [],
  },
  openDealsDataError: undefined,
  openDealsDataLoading: false,
  recentCompaniesCriteria: {
    limit: 10,
    usersIds: [],
  },
  recentCompaniesData: [],
  recentCompaniesDataError: undefined,
  recentCompaniesDataLoading: false,
  recordsBySourceList: [],
  recordsBySourceListLoading: false,
  recordsBySourceSummary: [],
  recordsOutOfCadence: {},
  recordsOutOfCadenceError: undefined,
  recordsOutOfCadenceFilter: {
    displayType: DisplayType.PERCENT,
    groupByType: GroupByType.USER,
    recordTypes: [EntityType.COMPANY, EntityType.DEAL, EntityType.PERSON],
  },
  recordsOutOfCadenceLoading: false,
  recordsSourceError: undefined,
  recordsSourceFilter: {
    displayType: DisplayType.GROSS,
    recordTypes: [EntityType.COMPANY, EntityType.PERSON, EntityType.DEAL],
    sourceTypes: [
      SourceType.PUBLIC_API,
      SourceType.WEB,
      SourceType.MOBILE,
      SourceType.IMPORTS,
      SourceType.INTEGRATIONS,
      SourceType.LEAD_GENERATION,
    ],
  },
  recordsSourceLoading: false,
  rottingDealsList: [],
  rottingDealsLoading: false,
  rottingDealsTotal: 0,
  selectedTypeData: [],
  todayActivitiesData: [],
  todayActivitiesDataError: undefined,
  todayActivitiesDataForReps: [],
  todayActivitiesDataForRepsError: undefined,
  todayActivitiesDataForRepsLoading: false,
  todayActivitiesDataLoading: false,
  totalRecordsByActivityType: 0,
  totalRecordsByEngagementList: 0,
  totalRecordsBySourceList: 0,
};
